import { ApiError } from "@station/api-client";
import type { FieldValues, Path, UseFormSetError } from "react-hook-form";

/**
 * Kullanıcıya gösterilecek, anlamlı Türkçe hata iletisi. problem+json `detail` varsa o kullanılır. `detail` yoksa
 * ama alan hataları varsa (ör. düğmeyle tetiklenen "Satırsız bütçe yürürlüğe alınamaz") başlığa onlar eklenir —
 * "Gönderilen veriler geçersiz" tek başına kullanıcıya ne yapacağını söylemez.
 */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const fieldMessages = [...new Set((error.problem.errors ?? []).map((item) => item.message))].slice(0, 3);
    const base =
      error.problem.detail ??
      (fieldMessages.length > 0
        ? `${error.problem.title}: ${fieldMessages.join(" · ")}`
        : error.problem.title);
    // Destek talebinde iletilebilsin diye beklenmeyen hatalarda istek kimliği eklenir.
    return error.problem.status >= 500 && error.problem.requestId
      ? `${base} (İstek no: ${error.problem.requestId})`
      : base;
  }
  if (error instanceof TypeError) return "Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol edin.";
  return error instanceof Error ? error.message : "Beklenmeyen bir hata oluştu.";
}

export function isApiError(error: unknown, code?: string): error is ApiError {
  return error instanceof ApiError && (code === undefined || error.code === code);
}

/**
 * Sunucunun alan bazındaki doğrulama hatalarını formdaki alanlara bağlar. Forma bağlanamayan hatalar
 * (bilinmeyen alan) geri döndürülür ki genel hata olarak gösterilsin — hiçbir hata sessizce kaybolmaz.
 */
export function applyFieldErrors<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  knownFields: readonly string[],
): string[] {
  if (!(error instanceof ApiError)) return [];
  const unmatched: string[] = [];
  for (const fieldError of error.problem.errors ?? []) {
    const path = fieldError.path.replace(/^body\./, "");
    if (knownFields.includes(path))
      setError(path as Path<T>, { type: "server", message: fieldError.message });
    else unmatched.push(fieldError.message);
  }
  return unmatched;
}
